class ApiResponseManager {
  constructor() {
    this.responseCache = new Map();
    this.maxCacheSize = 50; // Maximum number of cached responses
    this.maxCacheAge = 5 * 60 * 1000; // 5 minutes
    this.cleanupInterval = null;
  }

  // Start automatic cleanup
  startCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredResponses();
    }, 60000); // Cleanup every minute
  }

  // Stop cleanup
  stopCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  // Cache API response
  cacheResponse(url, params, response) {
    const key = this.generateCacheKey(url, params);
    const now = Date.now();

    // Remove oldest entries if cache is full
    if (this.responseCache.size >= this.maxCacheSize) {
      const oldestKey = this.responseCache.keys().next().value;
      this.responseCache.delete(oldestKey);
    }

    this.responseCache.set(key, {
      response,
      timestamp: now,
      size: this.calculateResponseSize(response),
    });

    console.log(`📦 Cached API response: ${url} (${this.calculateResponseSize(response)} bytes)`);
  }

  // Get cached response
  getCachedResponse(url, params) {
    const key = this.generateCacheKey(url, params);
    const cached = this.responseCache.get(key);

    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < this.maxCacheAge) {
        console.log(`📦 Using cached response: ${url}`);
        return cached.response;
      } else {
        this.responseCache.delete(key);
      }
    }

    return null;
  }

  // Clean up expired responses
  cleanupExpiredResponses() {
    const now = Date.now();
    let cleanedCount = 0;
    let totalSizeCleaned = 0;

    for (const [key, cached] of this.responseCache.entries()) {
      const age = now - cached.timestamp;
      if (age > this.maxCacheAge) {
        totalSizeCleaned += cached.size;
        this.responseCache.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(
        `🧹 Cleaned up ${cleanedCount} expired API responses (${totalSizeCleaned} bytes)`
      );
    }
  }

  // Clear all cached responses
  clearAllResponses() {
    let totalSize = 0;
    for (const cached of this.responseCache.values()) {
      totalSize += cached.size;
    }

    this.responseCache.clear();
    console.log(`🧹 Cleared all API response cache (${totalSize} bytes)`);
  }

  // Clear responses for specific route
  clearResponsesForRoute(routeName) {
    const routePatterns = {
      EntityData: ['entity', 'data', 'fields'],
      Dashboard: ['dashboard', 'charts', 'analytics'],
      FormBuilderList: ['form', 'builder'],
      Templates: ['template', 'form'],
    };

    const patterns = routePatterns[routeName] || [];
    let cleanedCount = 0;
    let totalSizeCleaned = 0;

    for (const [key, cached] of this.responseCache.entries()) {
      const shouldClean = patterns.some((pattern) => key.includes(pattern));
      if (shouldClean) {
        totalSizeCleaned += cached.size;
        this.responseCache.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(
        `🧹 Cleared ${cleanedCount} API responses for route ${routeName} (${totalSizeCleaned} bytes)`
      );
    }
  }

  // Generate cache key
  generateCacheKey(url, params) {
    const paramString = params ? JSON.stringify(params) : '';
    return `${url}${paramString}`;
  }

  // Calculate response size
  calculateResponseSize(response) {
    try {
      return JSON.stringify(response).length * 2; // Rough estimate (UTF-16)
    } catch (error) {
      return 0;
    }
  }

  // Get cache statistics
  getCacheStats() {
    let totalSize = 0;
    let totalCount = this.responseCache.size;

    for (const cached of this.responseCache.values()) {
      totalSize += cached.size;
    }

    return {
      count: totalCount,
      size: totalSize,
      maxSize: this.maxCacheSize,
      maxAge: this.maxCacheAge,
    };
  }
}

export default new ApiResponseManager();
